import { Stack } from '@mui/material'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  buildTeamExternalIntegration,
  type StatusCheckFormValues
} from 'components/status-checks//status-checks-form-utils'
import { isObservabilityToolOfType } from 'components/status-checks/authentication-form-validation-utils'
import { PollingIntervalField } from 'components/status-checks/common/PollingIntervalField'
import { StatusCheckCategoryField } from 'components/status-checks/common/StatusCheckCategoryField'
import TestStatusCheck from 'components/status-checks/form/status-check/common/TestStatusCheck'
import { StatusCheckConnectionFormFields } from 'components/status-checks/form/status-check/StatusCheckConnectionFormFields'
import {
  StatusCheckCategoryFieldVisibilityState,
  useGetCategoryFieldVisibilityState
} from 'components/status-checks/hooks/use-get-category-field-visibility-state'
import { useIsDynatraceEnhancedStatusCheck } from 'components/status-checks/hooks/use-is-dynatrace-enhanced-status-check'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'

const fieldsToWatch = [
  'dynatraceSelector.type',
  'endpointConfiguration',
  'evaluationConfiguration',
  'observabilityTool'
] as const

export const StatusCheckConnectionForm: React.FC = () => {
  const { isTestStatusCheckDisabled, handleSuccess } =
    useStatusCheckDetailsContext()

  const { control, getValues } = useFormContext<StatusCheckFormValues>()
  const [
    dynatraceSelectorType,
    endpointConfiguration,
    evaluationConfiguration,
    observabilityTool
  ] = useWatch({
    control,
    name: fieldsToWatch
  })

  // Determine Category field visibility state and related flag derivations
  const getCategoryFieldVisibilityState = useGetCategoryFieldVisibilityState()
  const categoryFieldVisibilityState = getCategoryFieldVisibilityState(
    observabilityTool,
    dynatraceSelectorType
  )

  const shouldShowCategoryField =
    categoryFieldVisibilityState !==
    StatusCheckCategoryFieldVisibilityState.HIDDEN
  const isCategoryFieldRequired =
    categoryFieldVisibilityState ===
    StatusCheckCategoryFieldVisibilityState.REQUIRED

  const [
    isAwsObservabilityTool,
    isCustomObservabilityTool,
    isDynatraceObservabilityTool,
    isPrometheusObservabilityTool
  ] = [
    ObservabilityToolType.AWS,
    ObservabilityToolType.CUSTOM,
    ObservabilityToolType.DYNATRACE,
    ObservabilityToolType.PROMETHEUS
  ].map((type) => isObservabilityToolOfType(type, observabilityTool))

  /*
   * The Dynatrace IHC case uses a different endpoint to test for configured entities.
   * We set this flag to denote during the evaluation request whether to use this method
   * vs. the default endpoint.
   */
  const shouldPerformDynatracePreFlightCheck =
    useIsDynatraceEnhancedStatusCheck()

  /*
   * 'AWS' & 'Custom' observability tool types have special connection test actions embedded into
   * each option, which reference the `TestTeamExternalIntegration` component. 'Dynatrace' type has a
   * separate pre-flight check action that is similarly handled within its variant layout.
   */
  const hasDefaultTestAction =
    !isAwsObservabilityTool &&
    !isCustomObservabilityTool &&
    !(isDynatraceObservabilityTool && shouldPerformDynatracePreFlightCheck)

  return (
    <Stack sx={{ rowGap: 4 }}>
      <StatusCheckConnectionFormFields />
      {hasDefaultTestAction && (
        <TestStatusCheck
          {...(isPrometheusObservabilityTool && { endpointConfiguration })}
          disabled={isTestStatusCheckDisabled}
          evaluationConfiguration={evaluationConfiguration}
          integration={buildTeamExternalIntegration(getValues())}
          onSuccess={handleSuccess}
        />
      )}
      <PollingIntervalField />
      {shouldShowCategoryField && (
        <StatusCheckCategoryField isRequired={isCategoryFieldRequired} />
      )}
    </Stack>
  )
}
